import { ExprFR } from './expr';
import type { RelFR } from './rel';

export const AXIS_SCALE_OPTIONS = [
  'linear',
  'day',
  'month',
  'year',
  'millisecond',
  'second',
  'minute',
  'hour',
  'point',
] as const;

export const VALUE_FORMAT_OPTIONS = [
  'none',
  'day',
  'month',
  'year',
  'millisecond',
  'second',
  'minute',
  'hour',
  '%',
  'number',
] as const;

export type ChartFormat = Record<
  string,
  (typeof VALUE_FORMAT_OPTIONS)[number] | string
>;

export type HeatmapColors = {
  readonly shade: '200' | '300' | '400' | '500';
  readonly color: 'Red' | 'Green' | 'Yellow' | 'Orange' | 'Indigo';
};

export type Axis = {
  readonly label: string | null;
  readonly scale: (typeof AXIS_SCALE_OPTIONS)[number] | null;
};

export type ChartAxis = {
  readonly x: Axis;
  readonly y: Axis;
};

export type _DataGrid = {
  readonly t: 'data-grid';
  readonly defaultRowCount: number;
};

export type _BarChart = {
  readonly t: 'bar-chart';
  readonly axis: ChartAxis;
  readonly trendline: boolean;
  readonly type: 'stacked' | 'grouped';
  readonly direction: 'vertical' | 'horizontal';
};

export type _Histogram = {
  readonly t: 'histogram-chart';
  readonly axis: ChartAxis;
  readonly type: 'stacked' | 'grouped';
  readonly direction: 'vertical' | 'horizontal';
};

export type _LineChart = {
  readonly t: 'line-chart';
  readonly axis: ChartAxis;
  readonly type: 'stacked' | 'normal' | 'area';
};

export type _ScatterPlot = {
  readonly t: 'scatter-plot';
  readonly axis: ChartAxis;
  readonly numTicks: number;
  readonly dynamicSizePoints: boolean;
};

export type _RelationInfo = {
  readonly t: 'relation-info';
};

export type _SummaryChart = {
  readonly t: 'summary-chart';
};

export type _LLMSummary = {
  readonly t: 'llm-summary';
  readonly prompt: string;
  readonly model: string | null;
};

export type _PieChart = {
  readonly t: 'pie-chart';
  readonly labels: boolean | null;
};

export type _RadarChart = {
  readonly t: 'radar-chart';
};

export type _HeatMapChart = {
  readonly t: 'heatmap-chart';
  readonly axis: ChartAxis;
  readonly colors: readonly HeatmapColors[];
};

export type _IndexList = {
  readonly t: 'index-list';
};

export type _SankeyChart = {
  readonly t: 'sankey-chart';
};

export type _SemanticSearchResults = {
  readonly t: 'semantic-search-results';
};

export type _ChartConfig =
  | _RadarChart
  | _BarChart
  | _DataGrid
  | _HeatMapChart
  | _Histogram
  | _LineChart
  | _PieChart
  | _RelationInfo
  | _SankeyChart
  | _ScatterPlot
  | _SummaryChart
  | _IndexList
  | _LLMSummary;

export type ChartSize = 'lg' | 'md' | 'sm';

export type _Chart = {
  readonly t: 'chart';
  readonly rel: RelFR;
  readonly title: ExprFR | null;
  readonly size: 'lg' | 'md' | 'sm';
  readonly config: _ChartConfig;
  readonly format: ChartFormat;
};
